import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useBankStore,
  useChartOfAccounts,
  useDeleteBank,
  useUpdateBank
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const SmallText = styled.span`
  color: #8e8e8e;
  font-size: 10px;
  font-weight: 500;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Textarea = styled.textarea`
  resize: none;
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  outline: none;
  padding: 8px;
  font-size: 12px;
  font-family: inherit;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  background-color: transparent;
`;

const bankAccountSchema = yup.object().shape({
  name: yup.string().required("Bank name is required"),
  account_number: yup.string().required("Account number is required"),
  description: yup
    .string()
    .max(50, "Description must be less than 50 characters"),
  chart_of_account_id: yup.string().required("Chart of account is required")
});

export function UpdateBankAccountModal() {
  const updateBankAccountOpen = useBankStore(
    state => state.updateBankAccountOpen
  );
  const closeUpdateBankAccountModal = useBankStore(
    state => state.closeUpdateBankAccountModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const selectedBank = useBankStore(state => state.selectedBank);

  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [
      {
        column: "account_type",
        operator: FILTER_OPERATIONS.EQUAL,
        value: "ASSET"
      }
    ]
  });

  const updateBankMutation = useUpdateBank();
  const deleteBankMutation = useDeleteBank();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: selectedBank?.name || "",
      account_number: selectedBank?.account_number || "",
      description: selectedBank?.description || "",
      chart_of_account_id: selectedBank?.chart_of_account_id || ""
    },
    resolver: yupResolver(bankAccountSchema)
  });

  useEffect(() => {
    if (selectedBank) {
      reset({
        name: selectedBank.name || "",
        account_number: selectedBank.account_number || "",
        description: selectedBank.description || "",
        chart_of_account_id: selectedBank.chart_of_account_id || ""
      });
    }
  }, [selectedBank, reset]);

  const onSubmit = data => {
    updateBankMutation.mutate({
      churchId: selectedChurch?.id,
      bankId: selectedBank?.id,
      bankData: data
    });
  };

  const handleDeleteBankAccount = () => {
    deleteBankMutation.mutate({
      churchId: selectedChurch?.id,
      bankId: selectedBank?.id
    });
  };

  return (
    <BaseModal
      title="Update bank account"
      isOpen={updateBankAccountOpen}
      onClose={closeUpdateBankAccountModal}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button
            flavor="neutral"
            size="small"
            onClick={handleDeleteBankAccount}
            disabled={
              updateBankMutation.isLoading || deleteBankMutation.isLoading
            }
          >
            Delete
          </Button>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              flavor="white"
              size="small"
              onClick={closeUpdateBankAccountModal}
              disabled={
                updateBankMutation.isLoading || deleteBankMutation.isLoading
              }
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleSubmit(onSubmit)}
              disabled={
                updateBankMutation.isLoading || deleteBankMutation.isLoading
              }
            >
              Save changes
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="name">Bank Name</Label>
        <Input id="name" {...register("name")} />
        <ErrorMessage
          errors={errors}
          name="name"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="account_number">Bank Account Number</Label>
        <Input id="account_number" {...register("account_number")} />
        <ErrorMessage
          errors={errors}
          name="account_number"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="chart_of_account_id">Chart of Account</Label>
        <Select id="chart_of_account_id" {...register("chart_of_account_id")}>
          <option value="" disabled>
            Select chart of account
          </option>
          {chartOfAccountsQuery.data?.result?.map(chartOfAccount => (
            <option key={chartOfAccount.id} value={chartOfAccount.id}>
              {chartOfAccount.account_name}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="chart_of_account_id"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea id="description" rows={5} {...register("description")} />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <SmallText>No more than 50 characters</SmallText>

      <MutationError error={updateBankMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
